/**
 * Whether a newer Babia is live, and what to do about it. The SPA is built
 * with its deploy's commit (scripts/build-client.ts) and the server reports
 * its own on /api/health. When they differ, a newer version is live: the app
 * shows a quiet notice, or reloads by itself when nothing can be lost. Pure,
 * so the rules are unit-tested (tests/update.test.ts); lib/app-update.tsx
 * runs the checks and reads the app's state.
 */

/**
 * This build's version, the deploy's commit. The dev server builds none in,
 * and an unset `process.env.…` throws in a browser (no `process`), so it is
 * empty there and nothing is ever compared.
 */
export const CLIENT_VERSION = builtVersion();

function builtVersion(): string {
  try {
    return process.env.BABIA_VERSION ?? "";
  } catch {
    return "";
  }
}

/** How often an open Babia asks for the server's version while it is visible. */
export const CHECK_EVERY_MS = 15 * 60_000;

/**
 * How far apart two checks must both see a new version before it counts.
 * While Railway switches instances, the old and the new server can both
 * answer for a few seconds, so one answer alone could be either.
 */
export const CONFIRM_AFTER_MS = 30_000;

/**
 * A server version unlike this build's: when a check first saw it, and when
 * a check last did, with no other answer in between.
 */
export interface Sighting {
  version: string;
  firstSeen: number;
  lastSeen: number;
}

/**
 * Fold one check's answer into the sighting. The same version again extends
 * it, another one starts over, and this build's own version (or no version on
 * either side) clears it.
 */
export function sight(
  sighting: Sighting | null,
  client: string,
  server: string,
  now: number,
): Sighting | null {
  if (!client || !server || server === client) return null;
  if (sighting?.version === server) return { ...sighting, lastSeen: now };
  return { version: server, firstSeen: now, lastSeen: now };
}

/** The newer version, once checks at least `CONFIRM_AFTER_MS` apart both saw it. */
export function newerVersion(sighting: Sighting | null): string | null {
  return sighting && sighting.lastSeen - sighting.firstSeen >= CONFIRM_AFTER_MS
    ? sighting.version
    : null;
}

/** When to check again to confirm a version seen only once, or null if nothing waits. */
export function confirmAt(sighting: Sighting | null): number | null {
  return sighting && !newerVersion(sighting) ? sighting.firstSeen + CONFIRM_AFTER_MS : null;
}

/** What the app is doing when a check's answer arrives. */
export interface AppState {
  /** The check ran because Babia just came back to the foreground. */
  returning: boolean;
  /** The parent tapped or typed since it came back. */
  touched: boolean;
  /** The log or the overview, where everything a parent types is in a sheet. */
  quietScreen: boolean;
  sheetOpen: boolean;
  /** An entry, a removal, or a setting that the server has not saved yet. */
  writing: boolean;
  /** A sheet holds words not yet sent, also while closed (the feedback draft). */
  draft: boolean;
  /** The version a reload already went for in this session, if any. */
  reloadedFor: string | null;
}

export type UpdateAction = "none" | "notice" | "reload";

/**
 * Whether a reload by itself loses nothing: Babia just came back to the
 * foreground on the log or the overview, untouched, with no sheet open, no
 * write in flight, and no unsent draft. Checked again just before the reload,
 * after its wait.
 */
export function quietMoment(app: AppState): boolean {
  return (
    app.returning && !app.touched && app.quietScreen && !app.sheetOpen && !app.writing && !app.draft
  );
}

/**
 * One check's answer: the new sighting, and what to do. Nothing until a newer
 * version is confirmed. Then a reload, in a quiet moment (`quietMoment`).
 * Otherwise the notice, which waits for a tap. A reload that already went for
 * this version and still got the old one shows the notice too, so a stale
 * shell can never cause a reload loop.
 */
export function decide(
  previous: Sighting | null,
  ids: { client: string; server: string },
  now: number,
  app: AppState,
): { sighting: Sighting | null; action: UpdateAction } {
  const sighting = sight(previous, ids.client, ids.server, now);
  const newer = newerVersion(sighting);
  if (!newer) return { sighting, action: "none" };
  return {
    sighting,
    action: quietMoment(app) && app.reloadedFor !== newer ? "reload" : "notice",
  };
}

/** A version as Settings shows it: the commit's first seven characters, as GitHub does. */
export function shortVersion(version: string): string {
  return version.slice(0, 7);
}
